
import { FC, useCallback, useState } from "react";
import { addMinutes } from "../timeline/helper";
import { CountdownTimer, MUIIcon, ProgressIndicator, useCalcRemainingTime } from "../../../widgets";
import { IExtended, useResendApprovalMutation, useUpdateApproveRejectMutation, useUpdateResendForModifiicationMutation } from "./properties/extendedApi";

interface IServiceRequestActionsProps {
    isSendingForApproval?: boolean,
    onCancel: (requestId: string) => void
    GPExtention?: IExtended,
    setErrorMessage?: any
}

export const ServiceRequestActions: FC<IServiceRequestActionsProps> = ({ GPExtention, onCancel, setErrorMessage }) => {
    const endTime = addMinutes(GPExtention?.sendTime, GPExtention?.otpTimer)
    const { isFuture } = useCalcRemainingTime({ endTime });
    const [cancelling, setCancelling] = useState<boolean>(false)
    const [cancelRecord] = useUpdateApproveRejectMutation();

    const isCancellable = (statusCode?: number) => {
        switch (statusCode) {
            case 1:  //pending
            case 365970001: // rejected
                return false
        }

        return true;
    }

    const resendButtons = (GPExtention?: IExtended, setErrorMessage?: any) => {
        if (!GPExtention?.sendTime || isFuture !== false)
            return <></>

        return <>
            <ServiceRequestResend GPExtention={GPExtention} setErrorMessage={setErrorMessage} />
        </>
    }

    const countdownTimer = useCallback((endTime?: Date) => {
        if (!endTime || isFuture !== true)
            return <></>

        return <>{
            GPExtention?.status === 365970002 &&
            <CountdownTimer endTime={endTime} label='Waiting for Approval' />
        }
        </>

        // eslint-disable-next-line
    }, [isFuture, GPExtention])

    const handleCancel = () => {
        if (!GPExtention) return;

        setCancelling(true)
        // if (!BoardingPass) return;

        const request: IExtended = {
            id: GPExtention?.id,
            stateCode: 1,
            status: 2
        }
        cancelRecord(request)
            .then(() => onCancel(GPExtention.id || ''))
            .finally(() => setCancelling(false))
    }

    const cancellationButton = () => {
        if (!isCancellable(GPExtention?.status))
            return <></>

        return <>
            <button
                disabled={cancelling}
                type="button" onClick={handleCancel} className='btn btn-sm btn-danger py-2 align-self-center'>
                {!cancelling && <>
                    <MUIIcon iconName={"CancelOutlined"} className="me-1" />
                    Cancel the request
                </>}
                {cancelling && <>
                    <ProgressIndicator show={true} message='Please wait...' />
                </>}
            </button>
        </>
    }

    if (!GPExtention)
        return <></>

    if (!GPExtention.sendTime && GPExtention?.status === 365970002)
        return <ProgressIndicator className="my-3" show={true} message='Sending approval request...' />

    return <div className='my-3'>
        <div className="d-flex gap-3">
            {countdownTimer(endTime)}
            <div className="flex-fill" />
            <div className="d-flex gap-3">
                {cancellationButton()}
                {resendButtons(GPExtention, setErrorMessage)}
            </div>
        </div>
    </div>
}


export const ServiceRequestResend = ({ GPExtention, setErrorMessage }: { GPExtention?: IExtended, setErrorMessage?: any }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingModify, setLoadingModify] = useState<boolean>(false);
    const [updateRecord] = useUpdateResendForModifiicationMutation();
    const [resendRequest] = useResendApprovalMutation();

    const handleResend = () => {
        setLoading(true)
        setErrorMessage("");
        resendRequest(GPExtention?.id || '').unwrap().catch((err) => {
            const message = err?.data?.message || "Error processing request";
            setErrorMessage(message);
        })
            .finally(() => setLoading(false))
    }

    const handleModification = () => {
        setLoadingModify(true);
        setErrorMessage("");
        const request: IExtended = {
            id: GPExtention?.id,
            status: 1,
        };

        updateRecord(request)
            .finally(() => setLoadingModify(false));
    }

    if (!GPExtention?.sendTime || GPExtention.status !== 365970002) //BoardingPass?.status !== ServiceRequestStatus.Submitted || !BoardingPass.sendTime
        return <></>

    return (
        <div>
            <div className="d-flex gap-3">
                <button
                    type="button" className="btn btn-sm btn-success py-2" onClick={handleModification} disabled={loadingModify}>
                    {loadingModify ?
                        <span className='indicator-progress' style={{ display: 'inline-block' }}>
                            Please wait...
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                        : <>
                            <span className='indicator-label'>
                                Resend for modification
                            </span>
                        </>}
                </button>
                <button
                    type="button" className="btn btn-sm btn-warning py-2" onClick={handleResend} disabled={loading}>
                    {loading ?
                        <span className='indicator-progress' style={{ display: 'inline-block' }}>
                            Please wait...
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                        : <>
                            <span className='indicator-label'>
                                Resend for approval
                            </span>
                        </>}
                </button>
            </div>
        </div>
    )
}